import React, { useEffect, useRef, useState } from "react";
import { Sparkles, Copy, Check, FileText, Save } from "lucide-react";
import { Page, Card, Pill, PrimaryButton, GhostButton, EmptyState } from "../components/UI";
import { NoteDrafts } from "../components/NoteDrafts";
import { secureSet, secureGet, secureList, secureDelete } from "../lib/secureStore";
import api from "../lib/api";
import { toast } from "sonner";

const FORMATS = ["SOAP", "DAP", "BIRP", "PIE"];
const TEMPLATES = ["individual", "group", "crisis", "medication", "supervision"];

export default function AINotes() {
  const [clients, setClients] = useState([]);
  const [notes, setNotes] = useState([]);
  const [form, setForm] = useState({ client_id: "", format: "SOAP", template: "individual", session_summary: "" });
  const [generating, setGenerating] = useState(false);
  const [active, setActive] = useState(null);
  const [copied, setCopied] = useState(false);
  const [editedContent, setEditedContent] = useState("");
  const [drafts, setDrafts] = useState([]);
  const [draftId, setDraftId] = useState(null);
  const [online, setOnline] = useState(navigator.onLine);

  const loadDrafts = async () => {
    const local = (await secureList("draft:").catch(() => [])).map((d) => ({ ...d, pending: true }));
    const remote = navigator.onLine ? (await api.get("/note-drafts")).data : [];
    setDrafts([...local, ...remote.filter((r) => !local.some((l) => l.local_id === r.local_id))]);
  };

  const syncing = useRef(false);
  const syncDrafts = async () => {
    if (syncing.current) return;
    syncing.current = true;
    try { await doSync(); } finally { syncing.current = false; }
  };
  const doSync = async () => {
    const local = await secureList("draft:").catch(() => []);
    for (const d of local) {
      const { pending, ...payload } = d;
      await api.post("/note-drafts", payload);
      await secureDelete(`draft:${d.local_id}`);
    }
    if (local.length) toast.success(`${local.length} offline draft${local.length > 1 ? "s" : ""} synced`);
  };

  useEffect(() => {
    if (navigator.onLine) {
      api.get("/clients").then(({ data }) => {
        setClients(data);
        secureSet("clients", data.map(({ id, first_name, last_name }) => ({ id, first_name, last_name }))).catch(() => {});
      });
      api.get("/notes").then(({ data }) => setNotes(data));
      syncDrafts().then(loadDrafts).catch(() => {});
    } else {
      secureGet("clients").then((c) => c && setClients(c)).catch(() => {});
      loadDrafts();
    }
    const goOnline = () => { setOnline(true); syncDrafts().then(loadDrafts).catch(() => {}); };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    const params = new URLSearchParams(window.location.search);
    const clientParam = params.get("client");
    if (clientParam) setForm((f) => ({ ...f, client_id: clientParam }));
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);

  const saveDraft = async () => {
    if (!form.session_summary.trim()) return toast.error("Write something before saving a draft");
    const local_id = draftId || crypto.randomUUID();
    const payload = { ...form, local_id };
    setDraftId(local_id);
    if (navigator.onLine) {
      await api.post("/note-drafts", payload);
      toast.success("Draft saved");
    } else {
      await secureSet(`draft:${local_id}`, payload);
      toast.success("Saved offline — encrypted on this device. It will sync when you're back online.");
    }
    loadDrafts();
  };

  const loadDraft = (d) => {
    setForm({ client_id: d.client_id || "", format: d.format, template: d.template, session_summary: d.session_summary });
    setDraftId(d.local_id);
    toast.info("Draft loaded");
  };

  const deleteDraft = async (d) => {
    if (d.pending) await secureDelete(`draft:${d.local_id}`);
    else await api.delete(`/note-drafts/${d.id}`);
    if (draftId === d.local_id) setDraftId(null);
    loadDrafts();
  };

  const generate = async () => {
    if (!form.client_id || !form.session_summary) return toast.error("Please select client and enter summary");
    setGenerating(true);
    try {
      const { data } = await api.post("/notes/generate", form);
      setNotes([data, ...notes]);
      setActive(data);
      setEditedContent(data.generated_content);
      setForm({ ...form, session_summary: "" });
      const used = drafts.find((d) => d.local_id === draftId && !d.pending);
      if (used) await api.delete(`/note-drafts/${used.id}`);
      setDraftId(null);
      loadDrafts();
      toast.success("Note generated");
    } catch (err) {
      toast.error("Failed to generate note");
    } finally {
      setGenerating(false);
    }
  };

  const saveNote = async () => {
    if (!active) return;
    try {
      await api.put(`/notes/${active.id}`, { final_content: editedContent, accepted: true });
      toast.success("Note saved");
      setNotes(notes.map(n => n.id === active.id ? { ...n, final_content: editedContent, accepted: true } : n));
    } catch { toast.error("Save failed"); }
  };

  const copy = () => {
    navigator.clipboard.writeText(editedContent || active?.final_content || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Copied");
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Page title="AI Progress Notes" subtitle="Bullet points in. Full clinical notes out.">
      <div className="grid lg:grid-cols-2 gap-5">
        <Card testid="note-composer" delay={0.05}>
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5" style={{ color: "var(--mf-accent)" }} />
            <h2 className="font-serif text-2xl">New Note</h2>
          </div>
          <div className="space-y-3">
            <select value={form.client_id} onChange={set("client_id")} className="w-full px-4 py-3 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="note-client-select">
              <option value="">Select client…</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
            </select>

            <div className="grid grid-cols-2 gap-3">
              <select value={form.format} onChange={set("format")} className="px-4 py-3 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="note-format-select">
                {FORMATS.map((f) => <option key={f}>{f}</option>)}
              </select>
              <select value={form.template} onChange={set("template")} className="px-4 py-3 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="note-template-select">
                {TEMPLATES.map((t) => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
              </select>
            </div>

            <textarea
              value={form.session_summary}
              onChange={set("session_summary")}
              rows={8}
              placeholder="Type or paste session bullet points… (e.g. 'Client reported improved sleep. Anxiety 4/10. Discussed coping strategies.')"
              className="w-full px-4 py-3 rounded-xl outline-none text-sm resize-none"
              style={{ background: "var(--mf-muted)" }}
              data-testid="note-summary-input"
            />

            <div className="flex flex-wrap items-center gap-2">
              <PrimaryButton onClick={generate} disabled={generating || !online} testid="note-generate-btn">
                {generating ? <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></span> Generating…</span> : <span className="flex items-center gap-2"><Sparkles className="w-4 h-4" /> Generate with AI</span>}
              </PrimaryButton>
              <GhostButton onClick={saveDraft} testid="note-save-draft-btn"><span className="flex items-center gap-2"><Save className="w-4 h-4" /> Save Draft</span></GhostButton>
            </div>
            {!online && <div className="text-xs" style={{ color: "var(--mf-accent)" }} data-testid="note-offline-hint">You're offline. Save a draft now — AI generation will be available once you reconnect.</div>}
          </div>
          <NoteDrafts drafts={drafts} clients={clients} onLoad={loadDraft} onDelete={deleteDraft} />
        </Card>

        <Card testid="note-preview" delay={0.1}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-2xl">Generated Note</h2>
            {active && (
              <div className="flex items-center gap-2">
                <button onClick={copy} className="p-2 rounded-lg hover:bg-black/5" data-testid="note-copy-btn">
                  {copied ? <Check className="w-4 h-4" style={{ color: "var(--mf-secondary)" }} /> : <Copy className="w-4 h-4" style={{ color: "var(--mf-text-muted)" }} />}
                </button>
                <PrimaryButton onClick={saveNote} testid="note-save-btn">Save & Accept</PrimaryButton>
              </div>
            )}
          </div>
          {!active ? (
            <div className="text-sm py-12 text-center" style={{ color: "var(--mf-text-muted)" }}>
              Your AI-generated note will appear here. Select a client, choose a format, and describe the session.
            </div>
          ) : (
            <textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              rows={16}
              className="w-full px-4 py-3 rounded-xl outline-none text-sm resize-none mf-prose"
              style={{ background: "var(--mf-muted)", color: "var(--mf-text)" }}
              data-testid="note-edit-area"
            />
          )}
        </Card>
      </div>

      <div className="mt-8">
        <h2 className="font-serif text-2xl mb-4">Recent Notes</h2>
        {notes.length === 0 ? (
          <EmptyState icon={FileText} title="No notes yet" description="Generate your first AI-powered clinical note above." />
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {notes.map((n, i) => (
              <div key={n.id} className="mf-card p-5 cursor-pointer mf-fade-in" style={{ animationDelay: `${Math.min(i * 0.05, 0.3)}s` }} onClick={() => { setActive(n); setEditedContent(n.final_content || n.generated_content); }} data-testid={`note-item-${n.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Pill variant="primary">{n.format}</Pill>
                    <Pill>{n.template}</Pill>
                    {n.accepted && <Pill variant="success">Accepted</Pill>}
                  </div>
                  <span className="text-xs" style={{ color: "var(--mf-text-muted)" }}>{new Date(n.created_at).toLocaleDateString()}</span>
                </div>
                <div className="text-sm line-clamp-3" style={{ color: "var(--mf-text)" }}>{n.final_content?.slice(0, 300)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Page>
  );
}
